import NumberFlow from '@number-flow/react';
import moment from 'moment';
import { type CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AnalyticsStatus, useAnalytics } from '@treams/treams-analytics';
import {
	SCORE_WINDOWS,
	scoreTranslation,
} from '@treams/treams-analytics/src/score/services/ScoreConfig';
import { useWorkspaceConfiguration } from '@treams/treams-hooks';
import { ChevronRightIcon } from '@treams/treams-icons';
import type * as graphql from '@treams/treams-network';
import {
	Card,
	Delta,
	Heading3,
	type ITimeMachine,
	LineChart,
	Paragraph,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import { AnalyticsRoute } from '../AnalyticsRoutes';
import { ScoreCategory, ScoreModuleState, type ScoreModuleStatus } from './TreamsScore.types';
import { useScoreModuleStatus } from './hooks/useScoreModuleStatus';
import { useTreamsScore } from './hooks/useTreamsScore';
import { formatDisplayScore, toDisplayPreviousScore, toDisplayScore } from './scoreDisplay';
import { buildSeriesEndDates, clampScoreEndDate, toScoreSeriesPoints } from './scoreSeries';
import { adjustScoresForModuleStatus, aggregateRowScores } from './scoreTotal';

export type TreamsScoreLocationState = {
	selectedGroupIds?: string[];
};

const CardLink = styled(Link)`
	display: flex;
	min-width: 0;
	text-decoration: none;
	color: inherit;
`;

const CardChevron = styled.span`
	display: inline-flex;
	align-items: center;
	color: ${tokens.icon.secondary};
	flex-shrink: 0;
	transition: color 0.15s ease;
`;

const InteractiveCard = styled(Card)`
	display: flex;
	flex-direction: column;
	width: 100%;
	min-width: 0;
	cursor: pointer;

	> div {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-width: 0;
	}

	&:hover {
		background: ${tokens.surface.hover};
		box-shadow: ${tokens.shadow.md};

		${CardChevron} {
			color: ${tokens.icon.primary};
		}
	}

	&:active {
		background: ${tokens.surface.secondary};
	}
`;

export function AnalyticsTreamsScoreOverviewCard(props: {
	timeMachine: ITimeMachine;
	groups: graphql.Group[];
	hasExplicitGroupSelection: boolean;
}) {
	const { t, i18n } = useTranslation();
	const workspaceConfiguration = useWorkspaceConfiguration();

	const workspaceStartDate = useMemo(
		() =>
			workspaceConfiguration.data?.startDate
				? moment(workspaceConfiguration.data.startDate).toDate()
				: undefined,
		[workspaceConfiguration.data?.startDate],
	);

	const { endDate, seriesEndDates } = useMemo(() => {
		const now = new Date();

		return {
			endDate: clampScoreEndDate(props.timeMachine.current[1], now),
			seriesEndDates: buildSeriesEndDates(props.timeMachine, workspaceStartDate, now),
		};
	}, [props.timeMachine, workspaceStartDate]);

	const {
		rows,
		series,
		isLoading: scoreLoading,
		engagementSurveyAvailable,
	} = useTreamsScore(endDate, seriesEndDates);

	const { status, loading: analyticsLoading } = useAnalytics();
	const loading = analyticsLoading || (status === AnalyticsStatus.Succeeded && scoreLoading);
	const analyticsUnavailable = !analyticsLoading && status !== AnalyticsStatus.Succeeded;

	const baseModuleStatus = useScoreModuleStatus();

	const moduleStatus = useMemo<ScoreModuleStatus>(
		() =>
			baseModuleStatus[ScoreCategory.Engagement] === ScoreModuleState.Disabled &&
			engagementSurveyAvailable
				? { ...baseModuleStatus, [ScoreCategory.Engagement]: ScoreModuleState.Enabled }
				: baseModuleStatus,
		[baseModuleStatus, engagementSurveyAvailable],
	);

	const filteredRows = useMemo(() => {
		if (!props.hasExplicitGroupSelection) return rows;

		const selectedIds = new Set(props.groups.map((group) => group.id));
		return rows.filter((row) => selectedIds.has(row.id));
	}, [rows, props.hasExplicitGroupSelection, props.groups]);

	const adjustedScores = useMemo(
		() => adjustScoresForModuleStatus(aggregateRowScores(filteredRows), moduleStatus),
		[filteredRows, moduleStatus],
	);

	const cardSeries = useMemo(
		() =>
			toScoreSeriesPoints(
				series,
				filteredRows.map((row) => row.id),
				moduleStatus,
				props.timeMachine.interval,
			),
		[series, filteredRows, moduleStatus, props.timeMachine.interval],
	);

	const displayTotal = toDisplayScore(adjustedScores.total.score);

	const periodStart = useMemo(
		() => moment(endDate).subtract(SCORE_WINDOWS.rollingWindowMonths, 'months').toDate(),
		[endDate],
	);

	const showDelta =
		filteredRows.some((row) => row.comparisonAvailable !== false) &&
		(!workspaceStartDate || !moment(periodStart).isBefore(workspaceStartDate, 'day'));

	const formatDate = (date: Date) => {
		const value = moment(date);
		if (i18n.language) value.locale(i18n.language);
		return value.format('D MMMM YYYY');
	};

	const linkState = useMemo<TreamsScoreLocationState | undefined>(
		() =>
			props.hasExplicitGroupSelection
				? { selectedGroupIds: props.groups.map((group) => group.id) }
				: undefined,
		[props.hasExplicitGroupSelection, props.groups],
	);

	return (
		<CardLink to={{ pathname: AnalyticsRoute.TreamsScore, state: linkState }}>
			<InteractiveCard chevron={false}>
				<Flex alignItems="center" justifyContent="space-between" mb={3}>
					<Heading3>{t('analytics.treamsScore.card.heading')}</Heading3>
					<CardChevron>
						<ChevronRightIcon size={20} color="currentColor" />
					</CardChevron>
				</Flex>

				<Flex
					flexDirection="column"
					alignItems="center"
					justifyContent="flex-start"
					width="100%"
					minWidth={0}
					flexGrow={1}
				>
					{loading ? (
						<Flex
							flexDirection="column"
							alignItems="center"
							width="100%"
							style={{ gap: tokens.gap.group }}
						>
							<Skeleton.Box width="100%" height={120} style={{ borderRadius: tokens.radius.sm }} />
							<Flex
								alignItems="center"
								justifyContent="center"
								flexWrap="wrap"
								width="100%"
								py={3}
								style={{
									alignContent: 'center',
									columnGap: tokens.gap.page,
									rowGap: tokens.gap.group,
								}}
							>
								<Flex flexDirection="column" alignItems="center" style={{ gap: tokens.gap.list }}>
									<Skeleton.Box width={96} height={64} style={{ borderRadius: tokens.radius.sm }} />
									<Skeleton.Box
										width={100}
										height={18}
										style={{ borderRadius: tokens.radius.sm }}
									/>
								</Flex>
								<Skeleton.Box width={160} height={26} style={{ borderRadius: tokens.radius.sm }} />
							</Flex>
						</Flex>
					) : analyticsUnavailable || filteredRows.length === 0 ? (
						<Paragraph color="secondary" textAlign="center">
							{t(
								analyticsUnavailable
									? 'analytics.cycles.notReady.title'
									: 'analytics.treamsScore.empty.title',
							)}
						</Paragraph>
					) : (
						<>
							{cardSeries.length > 1 && (
								<Box width="100%" minWidth={0} style={{ alignSelf: 'stretch' }}>
									<LineChart
										labels={cardSeries.map((point) => point.label)}
										data={cardSeries.map((point) => point.value)}
										bounds={[0, 100]}
										tintColor={tokens.brand.icon}
										highlightLastPoint
										compactLayout
										showBackground
										yFormat={(value) => formatDisplayScore(value)}
									/>
								</Box>
							)}
							<Flex
								flexDirection="row"
								alignItems="center"
								justifyContent="center"
								flexWrap="wrap"
								flexGrow={1}
								width="100%"
								py={3}
								style={{
									alignContent: 'center',
									columnGap: tokens.gap.page,
									rowGap: tokens.gap.group,
								}}
							>
								<Flex flexDirection="column" alignItems="center">
									<Box
										style={
											{
												fontFamily: tokens.typography.displayFamily,
												fontSize: 80,
												fontWeight: 900,
												lineHeight: 0.72,
												color: tokens.text.primary,
												'--number-flow-mask-height': '0px',
											} as CSSProperties
										}
									>
										<NumberFlow
											value={displayTotal}
											format={{ maximumFractionDigits: 0 }}
											locales="en-US"
										/>
									</Box>
									<Box
										style={{
											marginTop: tokens.gap.group,
											fontSize: tokens.typography.sizes.h3,
											fontWeight: tokens.typography.weights.medium,
											lineHeight: 1,
											color: tokens.text.primary,
										}}
									>
										{t(scoreTranslation(displayTotal))}
									</Box>
								</Flex>
								{showDelta && (
									<Delta
										currentValue={displayTotal}
										previousValue={toDisplayPreviousScore(
											adjustedScores.total.score,
											adjustedScores.total.delta,
										)}
										unit="numeric"
										animate
										label={t('analytics.treamsScore.card.comparedTo', {
											date: formatDate(periodStart),
										})}
									/>
								)}
							</Flex>
						</>
					)}
				</Flex>
			</InteractiveCard>
		</CardLink>
	);
}
